import React from 'react';

interface Props {
  min: number;
  max: number;
  value: number;
  onChange: (v: number) => void;
  isPlaying: boolean;
  onPlayPause: () => void;
  speed: number;
  onSpeedChange: (s: number) => void;
  period: '24h' | '3d' | '1w' | '2w' | '1M' | '3M';
  onPeriodChange: (p: '24h' | '3d' | '1w' | '2w' | '1M' | '3M') => void;
}

function formatTs(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleString('ja-JP', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const TimelineSlider: React.FC<Props> = ({
  min, max, value, onChange,
  isPlaying, onPlayPause,
  speed, onSpeedChange,
  period, onPeriodChange,
}) => {
  const pct = max === min ? 0 : ((value - min) / (max - min)) * 100;

  return (
    <div style={{
      background: 'linear-gradient(135deg,#0f172a 0%,#1e293b 100%)',
      borderTop: '1px solid #334155',
      padding: '12px 24px 16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    }}>
      {/* 上段：期間・速度・再生 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <span style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600, letterSpacing: '0.05em' }}>期間</span>
        {([
          { key: '24h', label: '24時間' },
          { key: '3d',  label: '3日' },
          { key: '1w',  label: '1週間' },
          { key: '2w',  label: '2週間' },
          { key: '1M',  label: '1ヶ月' },
          { key: '3M',  label: '3ヶ月' },
        ] as const).map(({ key, label }) => (
          <button key={key} onClick={() => onPeriodChange(key)} style={{
            padding: '4px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
            border: period === key ? 'none' : '1px solid #334155',
            background: period === key ? 'linear-gradient(90deg,#0ea5e9,#38bdf8)' : 'transparent',
            color: period === key ? '#fff' : '#94a3b8',
            transition: 'all .2s',
          }}>{label}</button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: '#94a3b8', fontSize: '12px' }}>速度</span>
          {[1,2,5,10].map(s => (
            <button key={s} onClick={() => onSpeedChange(s)} style={{
              padding: '3px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
              border: speed === s ? 'none' : '1px solid #334155',
              background: speed === s ? '#0ea5e9' : 'transparent',
              color: speed === s ? '#fff' : '#64748b',
            }}>{s}x</button>
          ))}
          <button onClick={onPlayPause} style={{
            width: '36px', height: '36px', borderRadius: '50%', border: 'none', cursor: 'pointer',
            background: 'linear-gradient(135deg,#0ea5e9,#0284c7)',
            color: '#fff', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 12px rgba(14,165,233,0.5)',
          }}>{isPlaying ? '⏸' : '▶'}</button>
        </div>
      </div>

      {/* スライダー */}
      <div style={{ position: 'relative', height: '28px', display: 'flex', alignItems: 'center' }}>
        <div style={{
          position: 'absolute', left: 0, right: 0, height: '4px',
          borderRadius: '2px', background: '#1e293b',
        }} />
        <div style={{
          position: 'absolute', left: 0, width: `${pct}%`, height: '4px',
          borderRadius: '2px', background: 'linear-gradient(90deg,#0ea5e9,#38bdf8)',
        }} />
        <input
          type="range" min={min} max={max} value={value}
          onChange={e => onChange(Number(e.target.value))}
          style={{ position: 'absolute', left: 0, right: 0, width: '100%', opacity: 0, cursor: 'pointer', height: '28px' }}
        />
        {/* つまみ */}
        <div style={{
          position: 'absolute', left: `calc(${pct}% - 10px)`,
          width: '20px', height: '20px', borderRadius: '50%',
          background: 'linear-gradient(135deg,#38bdf8,#0ea5e9)',
          border: '3px solid #0f172a',
          boxShadow: '0 0 10px rgba(14,165,233,0.7)',
          pointerEvents: 'none',
          transition: 'left .05s',
        }} />
      </div>

      {/* 時刻表示 */}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ color: '#475569', fontSize: '11px' }}>{formatTs(min)}</span>
        <span style={{
          color: '#38bdf8', fontSize: '13px', fontWeight: 700,
          background: 'rgba(14,165,233,0.15)', padding: '2px 12px', borderRadius: '6px',
          border: '1px solid rgba(14,165,233,0.3)',
        }}>{formatTs(value)}</span>
        <span style={{ color: '#475569', fontSize: '11px' }}>{formatTs(max)}</span>
      </div>
    </div>
  );
};

export default TimelineSlider;
